import {
  getSupportedModelConfig,
  getSupportedModelConfigs,
} from "@app/lib/llms/model_configurations";
import { isPremiumOrAboveTier } from "@app/lib/model_tiers/tier_order";
import type {
  EnabledModelConfigurationType,
  ModelStreamResolutionsType,
} from "@app/types/api/assistant/models";
import type { AgentModelConfigurationType } from "@app/types/assistant/agent";
import type { ModelStreamIdType } from "@app/types/assistant/models/auto";
import {
  AUTO_COMPLEX_MODEL_ID,
  AUTO_FAST_MODEL_ID,
  AUTO_MODEL_ID,
  isModelStreamId,
} from "@app/types/assistant/models/auto";
import type { ModelsTierName } from "@app/types/assistant/models/model_tiers";
import {
  getTierForModel,
  STATIC_MODEL_SUPPORTED_REASONING_EFFORTS,
} from "@app/types/assistant/models/model_tiers";
import { isStaticModelId } from "@app/types/assistant/models/models";
import { ORDERED_REASONING_EFFORTS } from "@app/types/assistant/models/reasoning";
import type {
  ModelConfigurationType,
  ModelIdType,
  ModelMakerIdType,
  ModelResolutionMethodType,
  ModelSelectionType,
  ReasoningEffort,
  ResolvedRequestedModel,
} from "@app/types/assistant/models/types";
import { getAvailableReasoningEfforts } from "@app/types/assistant/models/types";
import type { RegionType } from "@app/types/region";
import { assertNeverAndIgnore } from "@app/types/shared/utils/assert_never";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";

type Translate = (descriptor: MessageDescriptor) => string;

// Shown when a whole-premium model row or a premium reasoning-effort stop is
// locked because the workspace is on a legacy (non usage-based) plan.
const PREMIUM_MODEL_LOCKED_TOOLTIP = msg`This option isn't available on your workspace's current plan. Contact your administrator to upgrade.`;

// Shown when a model row is locked because the model's tier is not enabled for
// the workspace's current model-tier ceiling (independent of the plan: this
// applies even on usage-based plans whose tier grants stop below the model).
const MODEL_TIER_LOCKED_TOOLTIP = msg`Your current model access doesn't include this option. Contact your administrator to get access.`;

export const AUTO_MODELS_DOC_URL = "https://getazimut.com";

export const AUTO_MODELS_HINT = msg`Azimut selects a model and automatically switches to another if the selected model is unstable.`;

export const REASONING_EFFORT_LABELS: Record<
  ReasoningEffort,
  MessageDescriptor
> = {
  none: msg({ message: "None", context: "reasoning effort" }),
  minimal: msg({ message: "Min", context: "reasoning effort" }),
  low: msg({ message: "Low", context: "reasoning effort" }),
  medium: msg({ message: "Medium", context: "reasoning effort" }),
  high: msg({ message: "High", context: "reasoning effort" }),
  xhigh: msg({ message: "xHigh", context: "reasoning effort" }),
  maximal: msg({ message: "Max", context: "reasoning effort" }),
};

export const LOWERCASE_REASONING_EFFORT_LABELS: Record<
  ReasoningEffort,
  MessageDescriptor
> = {
  none: msg({ message: "none", context: "reasoning effort, lowercase" }),
  minimal: msg({ message: "min", context: "reasoning effort, lowercase" }),
  low: msg({ message: "low", context: "reasoning effort, lowercase" }),
  medium: msg({ message: "medium", context: "reasoning effort, lowercase" }),
  high: msg({ message: "high", context: "reasoning effort, lowercase" }),
  xhigh: msg({ message: "xhigh", context: "reasoning effort, lowercase" }),
  maximal: msg({ message: "max", context: "reasoning effort, lowercase" }),
};

export const MODELS_TIER_DISPLAY_NAMES: Record<
  ModelsTierName,
  MessageDescriptor
> = {
  cost_efficient: msg({ message: "Basic", context: "model tier" }),
  balanced: msg({ message: "Standard", context: "model tier" }),
  premium: msg({ message: "Premium", context: "model tier" }),
  ultra: msg({ message: "Ultra", context: "model tier" }),
};

export function getDegradedModelTooltip(
  t: Translate,
  displayName: string
): string {
  return t(
    msg`${displayName} is unstable right now. You may want to select another model.`
  );
}

export function getTierFallbackMessage(
  t: Translate,
  tierName: string,
  replacementModelName: string
): string {
  return t(
    msg`Preferred model for ${tierName} is unstable. Using ${replacementModelName} temporarily.`
  );
}

const PINNED_MODEL_RETRY_ERROR_CATEGORIES = [
  "retryable_model_error",
  "provider_internal_error",
  "stream_error",
  "empty_content",
] as const;

// A tier without a picker row maps to the closest row below it.
const PICKER_TIER_BY_MODELS_TIER: Record<ModelsTierName, ModelTierId> = {
  cost_efficient: "fast",
  balanced: "standard",
  premium: "complex",
  ultra: "complex",
};

/**
 * @cc [owner:frankaloia,label:product;error-handling] pinned-model-retry-uses-tier
 * When a model-related failure ran a pinned (non-stream) model, the failure UI MUST offer retry
 * on that model's tier, or on the closest tier below it that has a picker row, and send that tier
 * as `modelSelection`. After that retry, the conversation's last requested model and the input-bar
 * picker MUST show that tier so subsequent messages use the same stream. Stream-resolved failures
 * and every other retry MUST send no override.
 * Stream resolution consults the current degraded set server-side (`retry-model-selection`).
 */
export function getPinnedModelRetryTier({
  failedModel,
  modelResolutionMethod,
  errorCategory,
}: {
  failedModel: ResolvedRequestedModel | undefined;
  modelResolutionMethod: ModelResolutionMethodType | null | undefined;
  errorCategory: unknown;
}): ModelTierId | null {
  if (!failedModel) {
    return null;
  }
  if (
    modelResolutionMethod === "fair_use_downgrade" ||
    (modelResolutionMethod !== undefined &&
      modelResolutionMethod !== null &&
      isModelStreamId(modelResolutionMethod))
  ) {
    return null;
  }
  if (
    !(PINNED_MODEL_RETRY_ERROR_CATEGORIES as readonly string[]).includes(
      errorCategory as string
    )
  ) {
    return null;
  }

  const tierName = getTierForModel(
    failedModel.modelId,
    failedModel.reasoningEffort
  );
  return tierName ? PICKER_TIER_BY_MODELS_TIER[tierName] : null;
}

/**
 * @cc [owner:Nils-Fedrigo,label:product] regional-flag-follows-availability
 * The picker shows `region`'s hosting flag on a model if and only if that
 * model's `regionalAvailability[region]` is `true`.
 */
export function isModelHostedInRegion(
  model: ModelConfigurationType,
  region: RegionType
): boolean {
  return model.regionalAvailability[region] === true;
}

// The three primary picks of the model picker. Each tier is backed by a
// meta-model that is resolved to a concrete model at message-send time. Tier ids
// keep the meta-model wording; `name` is what users see:
//   - "Basic"     -> auto_fast (curated pool of small, cheap models)
//   - "Standard"  -> auto       (Azimut picks any available model — the old "Auto")
//   - "Premium"   -> auto_complex  (curated pool of powerful models)
export type ModelTierId = "fast" | "standard" | "complex";

export interface ModelTierDefinition {
  id: ModelTierId;
  metaModelId: ModelStreamIdType;
  name: MessageDescriptor;
}

export const MODEL_TIERS: ModelTierDefinition[] = [
  {
    id: "fast",
    metaModelId: AUTO_FAST_MODEL_ID,
    name: MODELS_TIER_DISPLAY_NAMES.cost_efficient,
  },
  {
    id: "standard",
    metaModelId: AUTO_MODEL_ID,
    name: MODELS_TIER_DISPLAY_NAMES.balanced,
  },
  {
    id: "complex",
    metaModelId: AUTO_COMPLEX_MODEL_ID,
    name: MODELS_TIER_DISPLAY_NAMES.premium,
  },
];

const TIER_BY_META_MODEL_ID: Record<ModelStreamIdType, ModelTierId> = {
  [AUTO_FAST_MODEL_ID]: "fast",
  [AUTO_MODEL_ID]: "standard",
  [AUTO_COMPLEX_MODEL_ID]: "complex",
};

export function getModelTier(tierId: ModelTierId): ModelTierDefinition {
  // MODEL_TIERS is exhaustive over ModelTierId, so a match is guaranteed; the
  // fallback to the "standard" tier is dead today and only guards against a
  // future tier being removed from the list.
  return (
    MODEL_TIERS.find((tier) => tier.id === tierId) ??
    MODEL_TIERS.find((tier) => tier.id === "standard") ??
    MODEL_TIERS[0]
  );
}

const PREMIUM_MODEL_TIER_IDS: ModelTierId[] = ["complex"];

// A tier row is locked either because the workspace is on a legacy plan without
// premium access, or because the stream's own model tier is above the member's
// cap — the server refuses such a selection, so the picker must not offer it.
export function getTierLockReason(
  tierId: ModelTierId,
  {
    lockPremiumEfforts,
    streamModels,
  }: {
    lockPremiumEfforts: boolean;
    streamModels: EnabledModelConfigurationType[];
  }
): ModelLockReason | null {
  if (lockPremiumEfforts && PREMIUM_MODEL_TIER_IDS.includes(tierId)) {
    return "premium";
  }

  const { metaModelId } = getModelTier(tierId);
  const streamModel = streamModels.find(
    (model) => model.modelId === metaModelId
  );
  // Absent means we can't tell yet: the tier rows render before the models
  // payload lands (`useModels` returns an empty list while it is in flight), so
  // locking on absence would flash every row locked on each open. Default to
  // unlocked — the server refuses an out-of-tier stream at send time anyway.
  if (streamModel && !streamModel.isSelectable) {
    return "model_tier";
  }

  return null;
}

export function getTierIdForMetaModelId(modelId: string): ModelTierId | null {
  return isModelStreamId(modelId) ? TIER_BY_META_MODEL_ID[modelId] : null;
}

export function getDefaultTierId(
  streamModels: EnabledModelConfigurationType[]
): ModelTierId {
  const standard = streamModels.find(
    (model) => model.modelId === AUTO_MODEL_ID
  );

  return standard && !standard.isSelectable ? "fast" : "standard";
}

export function formatModelEffortLabel(
  t: Translate,
  displayName: string,
  effort: ReasoningEffort
): string {
  if (effort === "none") {
    return displayName;
  }
  const effortLabel = t(REASONING_EFFORT_LABELS[effort]);

  return t(
    msg({
      message: `${displayName} ${effortLabel}`,
      context: "model name followed by its reasoning effort",
    })
  );
}

export function getTierResolvedModelLabel(
  t: Translate,
  tierId: ModelTierId,
  streams: ModelStreamResolutionsType | null
): string | undefined {
  const resolution = streams?.[getModelTier(tierId).metaModelId];
  if (!resolution) {
    return undefined;
  }
  return formatModelEffortLabel(
    t,
    resolution.displayName,
    resolution.reasoningEffort
  );
}

// `isModelHostedInRegion` for the concrete model a tier currently resolves to.
// That resolution moves as models are added or degraded, so the flag speaks for
// the resolution the row is displaying and nothing more.
export function isTierResolvedModelHostedInRegion(
  tier: ModelTierDefinition,
  streams: ModelStreamResolutionsType | null,
  region: RegionType
): boolean {
  const resolution = streams?.[tier.metaModelId];
  if (!resolution) {
    return false;
  }
  const model = getSupportedModelConfigs().find(
    (config) =>
      config.providerId === resolution.providerId &&
      config.modelId === resolution.modelId
  );

  return model !== undefined && isModelHostedInRegion(model, region);
}

// What the picker is currently showing, decoupled from the payload we send:
// a tier or a concrete model + reasoning effort.
export type SelectionDisplay =
  | { kind: "tier"; tierId: ModelTierId }
  | { kind: "model"; model: ModelConfigurationType; effort: ReasoningEffort };

// A resolved picker selection. `toSend` is `undefined` when the selection is
// the untouched agent default: we then send no override and let the backend use
// the agent's own configured model/effort.
export interface Selection {
  display: SelectionDisplay;
  toSend: ModelSelectionType | undefined;
}

export interface MakerGroup {
  makerId: ModelMakerIdType;
  models: ModelConfigurationType[];
}

// A `SelectionDisplay` whose effort may be absent: the manage-agents filter
// names a model without picking an effort for it.
export type SelectedEntry =
  | { kind: "tier"; tierId: ModelTierId }
  | {
      kind: "model";
      model: ModelConfigurationType;
      effort: ReasoningEffort | null;
    };

export type SelectedModelEntry = Extract<SelectedEntry, { kind: "model" }>;

// What the menu highlights. The conversation picker passes a single display
// plus the agent default; the manage-agents filter passes one per active
// filter and no default.
export interface ModelPickerSelectionModel {
  selected: SelectedEntry[];
  agentDefault: SelectionDisplay | null;
  // Present only when the active selection can be reverted to the default.
  onRevert?: () => void;
}

export type ModelLockReason = "premium" | "model_tier";

// One stop of the reasoning-effort slider. A null reason means it is available;
// otherwise the effort is locked behind access controls.
export interface EffortStop {
  effort: ReasoningEffort;
  unavailabilityReason: ModelLockReason | null;
}

export function buildTierSelection(tierId: ModelTierId): ModelSelectionType {
  const { metaModelId } = getModelTier(tierId);
  return {
    providerId: metaModelId,
    modelId: metaModelId,
    reasoningEffort: "none",
  };
}

export function buildModelSelection(
  model: ModelConfigurationType,
  effort: ReasoningEffort
): ModelSelectionType {
  return {
    providerId: model.providerId,
    modelId: model.modelId,
    reasoningEffort: effort,
  };
}

export function getModelKey(providerId: string, modelId: string): string {
  return `${providerId}/${modelId}`;
}

export function isModelSelection(
  model: ModelConfigurationType,
  display: SelectedEntry
): boolean {
  return (
    display.kind === "model" &&
    display.model.providerId === model.providerId &&
    display.model.modelId === model.modelId
  );
}

export function isTierDisplayed(
  tierId: ModelTierId,
  display: SelectedEntry
): boolean {
  return display.kind === "tier" && display.tierId === tierId;
}

export function isTierSelected(
  tierId: ModelTierId,
  selection: ModelPickerSelectionModel
): boolean {
  return selection.selected.some((display) => isTierDisplayed(tierId, display));
}

export function findSelectedModelEntry(
  model: ModelConfigurationType,
  selection: ModelPickerSelectionModel
): SelectedModelEntry | undefined {
  return selection.selected.find(
    (entry): entry is SelectedModelEntry =>
      entry.kind === "model" && isModelSelection(model, entry)
  );
}

// Display equality ignoring reasoning effort: two model displays for the same
// model are "the same" regardless of effort. Used to highlight the selected row
// and to mark the default, where effort is surfaced by the slider instead.
function isSameDisplay(a: SelectionDisplay, b: SelectionDisplay): boolean {
  if (a.kind === "tier" && b.kind === "tier") {
    return a.tierId === b.tierId;
  }
  if (a.kind === "model" && b.kind === "model") {
    return (
      a.model.providerId === b.model.providerId &&
      a.model.modelId === b.model.modelId
    );
  }
  return false;
}

// Full selection equality, effort included. Used to decide whether the active
// selection is exactly the agent default (so no override needs to be kept, and
// the revert affordance is hidden).
export function isSameSelection(
  a: SelectionDisplay,
  b: SelectionDisplay
): boolean {
  if (a.kind === "model" && b.kind === "model") {
    return isSameDisplay(a, b) && a.effort === b.effort;
  }
  return isSameDisplay(a, b);
}

interface LockPremiumOptions {
  lockPremiumEfforts?: boolean;
}

function modelSupportsEffortStatically(
  modelId: ModelIdType,
  effort: ReasoningEffort
): boolean {
  if (!isStaticModelId(modelId)) {
    return false;
  }
  return STATIC_MODEL_SUPPORTED_REASONING_EFFORTS[modelId][effort];
}

// The single authority for whether a reasoning-effort level is selectable. The
// stops are the efforts the model supports, so efforts it lacks never show. A
// static model without reasoning has no slider, hence no stops.
export function getEffortStops(
  enabledModel: ModelConfigurationType,
  { lockPremiumEfforts = false }: LockPremiumOptions = {}
): EffortStop[] {
  if (
    isStaticModelId(enabledModel.modelId) &&
    !isReasoningModel(enabledModel.modelId)
  ) {
    return [];
  }

  const allowed = new Set(
    getAvailableReasoningEfforts(enabledModel.supportedReasoningEfforts)
  );

  return ORDERED_REASONING_EFFORTS.flatMap((effort): EffortStop[] => {
    if (!allowed.has(effort)) {
      return modelSupportsEffortStatically(enabledModel.modelId, effort)
        ? [{ effort, unavailabilityReason: "model_tier" }]
        : [];
    }
    if (
      lockPremiumEfforts &&
      isPremiumOrAboveTier(getTierForModel(enabledModel.modelId, effort))
    ) {
      return [{ effort, unavailabilityReason: "premium" }];
    }
    return [{ effort, unavailabilityReason: null }];
  });
}

// The reasoning effort to use when a model is freshly selected: its default when
// that is allowed, otherwise the first available stop.
export function getInitialEffort(
  enabledModel: ModelConfigurationType,
  options: LockPremiumOptions = {}
): ReasoningEffort {
  const stops = getEffortStops(enabledModel, options);
  const preferred = stops.find(
    (stop) =>
      stop.effort === enabledModel.defaultReasoningEffort &&
      stop.unavailabilityReason === null
  );
  if (preferred) {
    return preferred.effort;
  }
  return (
    stops.find((stop) => stop.unavailabilityReason === null)?.effort ?? "none"
  );
}

function isReasoningModel(modelId: ModelIdType): boolean {
  if (!isStaticModelId(modelId)) {
    return false;
  }
  const support = STATIC_MODEL_SUPPORTED_REASONING_EFFORTS[modelId];
  return ORDERED_REASONING_EFFORTS.some(
    (effort) => effort !== "none" && support[effort]
  );
}

// Whether a whole model row must be locked, and why: a reasoning model left
// with no usable slider effort, or, on a legacy plan, a model whose every
// effort is Premium or above.
export function isModelLocked(
  enabledModel: ModelConfigurationType,
  { lockPremiumEfforts }: { lockPremiumEfforts: boolean }
): boolean {
  const stops = getEffortStops(enabledModel, { lockPremiumEfforts });
  const hasUsableSliderEffort = stops.some(
    (stop) => stop.unavailabilityReason === null
  );

  if (isReasoningModel(enabledModel.modelId) && !hasUsableSliderEffort) {
    return true;
  }

  if (!lockPremiumEfforts) {
    return false;
  }
  if (stops.length > 0) {
    return stops.every((stop) => stop.unavailabilityReason === "premium");
  }
  return isPremiumOrAboveTier(getTierForModel(enabledModel.modelId, "none"));
}

export function getModelLockReason(
  enabledModel: ModelConfigurationType,
  { lockPremiumEfforts }: { lockPremiumEfforts: boolean }
): ModelLockReason | null {
  if (!isModelLocked(enabledModel, { lockPremiumEfforts })) {
    return null;
  }
  return lockPremiumEfforts ? "premium" : "model_tier";
}

export function getModelLockTooltip(
  t: Translate,
  reason: ModelLockReason
): string {
  switch (reason) {
    case "premium":
      return t(PREMIUM_MODEL_LOCKED_TOOLTIP);
    case "model_tier":
      return t(MODEL_TIER_LOCKED_TOOLTIP);
    default:
      assertNeverAndIgnore(reason);
      return "";
  }
}

export function getEffortStopTooltip(
  t: Translate,
  stop: EffortStop
): string | null {
  switch (stop.unavailabilityReason) {
    case "premium":
      return t(PREMIUM_MODEL_LOCKED_TOOLTIP);
    case "model_tier":
      return t(MODEL_TIER_LOCKED_TOOLTIP);
    case null:
      return null;
    default:
      assertNeverAndIgnore(stop.unavailabilityReason);
      return null;
  }
}

export function getModelWithReasoningEffortLabel(
  t: Translate,
  display: SelectionDisplay
): string {
  switch (display.kind) {
    case "tier":
      return t(getModelTier(display.tierId).name);
    case "model": {
      const { model, effort } = display;
      return formatModelEffortLabel(t, model.displayName, effort);
    }
    default:
      assertNeverAndIgnore(display);
      return "";
  }
}

function findAvailableModel(
  models: ModelConfigurationType[],
  selection: { providerId: string; modelId: string }
): ModelConfigurationType | undefined {
  return models.find(
    (m) =>
      m.providerId === selection.providerId && m.modelId === selection.modelId
  );
}

function findAgentModel(
  models: ModelConfigurationType[],
  agentModel: AgentModelConfigurationType
): ModelConfigurationType | undefined {
  return (
    findAvailableModel(models, agentModel) ??
    getSupportedModelConfig(agentModel) ??
    undefined
  );
}

function resolveRequestedSelection(
  models: ModelConfigurationType[],
  selection: ModelSelectionType | null | undefined
): Selection | null {
  if (!selection) {
    return null;
  }
  if (isModelStreamId(selection.modelId)) {
    const tierId = TIER_BY_META_MODEL_ID[selection.modelId];
    return {
      display: { kind: "tier", tierId },
      toSend: buildTierSelection(tierId),
    };
  }
  const model = findAvailableModel(models, selection);
  if (!model) {
    return null;
  }
  const effort = selection.reasoningEffort ?? getInitialEffort(model);
  return {
    display: { kind: "model", model, effort },
    toSend: buildModelSelection(model, effort),
  };
}

function resolveAgentDefault({
  agentModel,
  models,
}: {
  agentModel: AgentModelConfigurationType | null;
  models: EnabledModelConfigurationType[];
}): Selection {
  const tierDefault: Selection = {
    display: { kind: "tier", tierId: getDefaultTierId(models) },
    toSend: undefined,
  };
  if (!agentModel) {
    return tierDefault;
  }
  if (isModelStreamId(agentModel.modelId)) {
    return {
      display: {
        kind: "tier",
        tierId: TIER_BY_META_MODEL_ID[agentModel.modelId],
      },
      toSend: undefined,
    };
  }
  const model = findAgentModel(models, agentModel);
  if (!model) {
    return tierDefault;
  }

  // Keep `toSend` undefined so the agent runs its own configured model/effort
  // server-side; the slider surfaces the agent's configured effort as-is.
  const effort = agentModel.reasoningEffort ?? getInitialEffort(model);
  return {
    display: { kind: "model", model, effort },
    toSend: undefined,
  };
}

// The selection to show, in precedence order: last-requested (per-conversation)
// > session sticky > agent default. The agent default is returned separately so
// callers can mark it "(Default)" and offer a revert affordance.
export function resolveShownSelection({
  agentModel,
  lastRequestedModel,
  sessionSticky,
  models,
}: {
  agentModel: AgentModelConfigurationType | null;
  lastRequestedModel: ModelSelectionType | null;
  sessionSticky?: ModelSelectionType | null;
  models: EnabledModelConfigurationType[];
}): { shown: Selection; agentDefault: Selection } {
  const agentDefault = resolveAgentDefault({ agentModel, models });
  const shown =
    resolveRequestedSelection(models, lastRequestedModel) ??
    resolveRequestedSelection(models, sessionSticky) ??
    agentDefault;
  return { shown, agentDefault };
}
